import { resolveOfflineOrderCode } from "../offline/orderCode";

const CACHE_HEADER = "x-billsarthi-desktop-cache";
const API_HOSTS = new Set(["qr.billsarthi.in"]);

const isDesktop = () =>
  typeof window !== "undefined" && Boolean(window.desktopApiCache);

const getRequestMethod = (input, init) =>
  String(init?.method || (input instanceof Request ? input.method : "GET")).toUpperCase();

const getRequestUrl = (input) =>
  typeof input === "string" || input instanceof URL
    ? String(input)
    : input?.url || "";

const readBody = async (input, init) => {
  if (typeof init?.body === "string") return init.body;
  if (input instanceof Request && !init?.body) return input.clone().text();
  return "";
};

const isReadEndpoint = (url, method, body) => {
  if (!API_HOSTS.has(url.hostname) || !url.pathname.includes("/public/api/")) return false;
  if (method === "GET") return true;
  if (method !== "POST" || body instanceof FormData) return false;

  // POST is used by the existing API for reads too. Keep mutations out of cache.
  return /\/(list|get|getorderlist|getorderdetail|report(?:-v2)?|summary|history|pricing|categories|entitlement-status|checkvalid|monthsummary|product\/variants\/list|product\/addons\/(list|map|by-product)|waiter\/kot\/history|referral\/info)(?:\/|$|\?)/i.test(
    url.pathname
  );
};

const getScope = (body) => {
  try {
    const parsed = JSON.parse(body || "{}");
    if (parsed?.restaurantid || parsed?.restaurantId) {
      return `restaurant:${parsed.restaurantid || parsed.restaurantId}`;
    }
  } catch (_) {}

  try {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (user?.id) return `restaurant:${user.id}`;
  } catch (_) {}
  return "anonymous";
};

const makeKey = ({ url, method, body, scope }) =>
  `${scope}|${method}|${url}|${body || ""}`;

const toCachedResponse = (entry) =>
  new Response(entry.body, {
    status: entry.status || 200,
    headers: {
      "content-type": entry.contentType || "application/json",
      [CACHE_HEADER]: "1",
    },
  });

const isOrderListRequest = (url) => /\/getorderlist$/i.test(url.pathname);
const isOrderDetailRequest = (url) => /\/getorderdetail$/i.test(url.pathname);

const localDateKey = (value) => {
  const raw = String(value || "").trim();
  const date = new Date(/^\d{4}-\d{2}-\d{2}\s/.test(raw) ? `${raw.replace(" ", "T")}Z` : raw);
  if (Number.isNaN(date.getTime())) return raw.slice(0, 10);
  const fields = new Intl.DateTimeFormat("en", {
    timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(date).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  return `${fields.year}-${fields.month}-${fields.day}`;
};

const readOrderListRequest = (body) => {
  try {
    const parsed = JSON.parse(body || "{}");
    return {
      restaurantId: parsed.restaurantid || parsed.restaurantId,
      fromDate: parsed.fromdate,
      toDate: parsed.todate,
    };
  } catch (_) {
    return {};
  }
};

const readOrderDetailRequest = (body) => {
  try {
    const parsed = JSON.parse(body || "{}");
    return {
      restaurantId: parsed.restaurantid || parsed.restaurantId,
      orderId: parsed.orderid || parsed.orderId || parsed.id,
    };
  } catch (_) {
    return {};
  }
};

const pendingOrderToListItem = (item) => ({
  id: `pending-${item.id}`,
  ordercode: resolveOfflineOrderCode(item),
  created_at: item.createdAt,
  tableid: item.payload?.tableid || 0,
  totalamount: item.payload?.totalPrice || 0,
  totalPrice: item.payload?.totalPrice || 0,
  payment_mode: item.payload?.payment_mode || item.payload?.mode || "Cash",
  mode: item.payload?.mode || item.payload?.payment_mode || "Cash",
  itemlist: item.payload?.itemlist || [],
  is_pending_sync: item.metadata?.localOnly !== true,
  is_local_only: item.metadata?.localOnly === true,
});

const addPendingOrders = async (result, request) => {
  if (!isDesktop() || !request?.restaurantId) return result;
  const pending = await window.desktopOrderQueue.list({
    restaurantId: request.restaurantId,
    includeDeferred: true,
  });
  const matchingPending = (pending || []).filter((item) => {
    if (!/\/create\/order$/i.test(String(item?.endpoint || ""))) return false;
    const date = localDateKey(item.createdAt);
    if (!request.fromDate || !request.toDate || !date) return true;
    return date >= request.fromDate && date <= request.toDate;
  });
  if (!matchingPending.length) return result;
  return {
    ...(result || {}),
    success: result?.success !== false,
    list: [...matchingPending.map(pendingOrderToListItem), ...(Array.isArray(result?.list) ? result.list : [])],
  };
};

const buildOrderListResponse = async (result, request, status = 200) => {
  const merged = await addPendingOrders(result, request);
  return new Response(JSON.stringify(merged), {
    status,
    headers: { "content-type": "application/json" },
  });
};

const getCachedOrderListResponse = async (request) => {
  if (!isDesktop() || !request?.restaurantId) return null;
  const cachedOrders = await window.desktopOrders.list(request);
  const response = await buildOrderListResponse(
    { success: true, list: cachedOrders || [], offline: true },
    request
  );
  const result = await response.clone().json().catch(() => null);
  return result?.list?.length ? response : null;
};

const persistFetchedOrders = async (request, response) => {
  if (!isDesktop() || !request?.restaurantId || !response?.ok) return;
  const json = await response.clone().json().catch(() => null);
  if (Array.isArray(json?.list)) {
    await window.desktopOrders.upsert({ restaurantId: request.restaurantId, orders: json.list });
  }
};

const persistFetchedOrderDetail = async (request, response) => {
  if (!isDesktop() || !request?.restaurantId || !request?.orderId || !response?.ok) return;
  const json = await response.clone().json().catch(() => null);
  if (!json) return;
  const order = json?.orderdetails || json?.order || {};
  const keys = [request.orderId, order?.id, order?.orderid, order?.order_id, order?.ordercode];
  await window.desktopOrderDetails.upsert({ restaurantId: request.restaurantId, keys, response: json });
};

const getCachedOrderDetailResponse = async (request) => {
  if (!isDesktop() || !request?.restaurantId || !request?.orderId) return null;
  const detail = await window.desktopOrderDetails.get(request);
  return detail
    ? new Response(JSON.stringify(detail), { status: 200, headers: { "content-type": "application/json", [CACHE_HEADER]: "1" } })
    : null;
};

const storeResponse = async (cacheKey, response) => {
  if (!response?.ok) return;
  const contentType = response.headers.get("content-type") || "";
  if (!/application\/json|text\/json/i.test(contentType)) return;
  const body = await response.clone().text();
  if (!body.trim()) return;
  await window.desktopApiCache.set({
    key: cacheKey,
    body,
    status: response.status,
    contentType,
  });
};

/**
 * Adds network-first reads with SQLite fallback for existing safe APIs.
 * It is installed only in Electron; the normal browser keeps native fetch.
 */
export const installElectronApiCache = () => {
  if (!isDesktop() || window.__billsArthiApiCacheInstalled) return;
  window.__billsArthiApiCacheInstalled = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const rawUrl = getRequestUrl(input);
    let url;
    try {
      url = new URL(rawUrl, window.location.href);
    } catch (_) {
      return nativeFetch(input, init);
    }

    const method = getRequestMethod(input, init);
    const body = await readBody(input, init);
    if (!isReadEndpoint(url, method, init?.body)) return nativeFetch(input, init);

    const cacheKey = makeKey({ url: url.toString(), method, body, scope: getScope(body) });
    const orderListRequest = isOrderListRequest(url) ? readOrderListRequest(body) : null;
    const orderDetailRequest = isOrderDetailRequest(url) ? readOrderDetailRequest(body) : null;
    try {
      const response = await nativeFetch(input, init);
      storeResponse(cacheKey, response).catch(() => {});
      if (orderListRequest) {
        // Do not return the live response until the normalized order rows are durable.
        // A user can turn off Wi-Fi immediately after this screen loads.
        await persistFetchedOrders(orderListRequest, response);
        const json = await response.clone().json().catch(() => null);
        if (json && response.ok) return buildOrderListResponse(json, orderListRequest, response.status);
        // API gateways sometimes return an error response without throwing. For desktop
        // sales/history screens, use last known SQLite data in that case as well.
        if (!response.ok) {
          const cachedOrders = await getCachedOrderListResponse(orderListRequest);
          if (cachedOrders) return cachedOrders;
        }
      }
      if (orderDetailRequest) {
        // Historical sync must not finish until the printable item detail is
        // durable in SQLite; item/category reports depend on this row.
        await persistFetchedOrderDetail(orderDetailRequest, response);
        if (!response.ok) {
          const cachedDetail = await getCachedOrderDetailResponse(orderDetailRequest);
          if (cachedDetail) return cachedDetail;
        }
      }
      return response;
    } catch (error) {
      try {
        if (orderDetailRequest) {
          const cachedDetail = await getCachedOrderDetailResponse(orderDetailRequest);
          if (cachedDetail) return cachedDetail;
        }
        if (orderListRequest?.restaurantId) {
          const cachedOrders = await getCachedOrderListResponse(orderListRequest);
          if (cachedOrders) return cachedOrders;
        }
        const cached = await window.desktopApiCache.get({ key: cacheKey });
        if (cached?.body) {
          const cachedResponse = toCachedResponse(cached);
          if (!orderListRequest) return cachedResponse;
          const json = await cachedResponse.json().catch(() => null);
          if (json) return buildOrderListResponse(json, orderListRequest);
        }
      } catch (cacheError) {
        console.warn("Unable to read Electron API cache", cacheError);
      }
      throw error;
    }
  };
};
